import { useEffect } from 'react';

import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { useFormatText } from '@/hooks/useFormatText';
import { updateSettings } from '@/service/settingsServices';
import { useSettingsStore } from '@/store/useSettingsStore';
import { TCurrency, TLanguage, TDateFormat, ISettingsData } from '@/types';

interface ILanguageProps {
  settingsData: ISettingsData | null;
  onRefresh?: () => void;
}

const Language = ({ settingsData, onRefresh }: ILanguageProps) => {
  const { formatText } = useFormatText();
  const { settings, updateSettings: updateStore } = useSettingsStore();

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'ar', label: 'العربية (Arabic)' },
  ];

  const dateFormatOptions = [
    {
      value: 'dd-mm-yyyy',
      label: 'DD/MM/YYYY',
    },
    { value: 'mm-dd-yyyy', label: 'MM/DD/YYYY' },
    { value: 'yyyy-mm-dd', label: 'YYYY/MM/DD' },
  ];

  useEffect(() => {
    if (settingsData) {
      updateStore({
        language: settingsData.language as TLanguage,
        direction: settingsData.language === 'ar' ? 'rtl' : 'ltr',
        dateFormat: settingsData.date_format as TDateFormat,
        currency: settingsData.currency as TCurrency,
      });
    }
  }, [settingsData, updateStore]);

  const handleLanguageChange = async (value: string | string[]) => {
    const lang = value as 'en' | 'ar';
    updateStore({
      language: lang,
      direction: lang === 'ar' ? 'rtl' : 'ltr',
    });

    if (settingsData) {
      const res = await updateSettings({
        language: lang,
        date_format: settingsData.date_format,
        currency: settingsData.currency,
      });
      if (res?.status) onRefresh?.();
    }
  };

  const handleDateFormatChange = async (value: string) => {
    const dateFormat = value as TDateFormat;
    updateStore({ dateFormat });

    if (settingsData) {
      const res = await updateSettings({
        language: settingsData.language,
        date_format: dateFormat,
        currency: settingsData.currency,
      });
      if (res?.status) onRefresh?.();
    }
  };

  return (
    <div className="flex max-md:flex-col gap-10 w-full">
      {/* Default Language */}
      <div className="flex-1">
        <h3 className="text-base font-semibold text-neutral-primary mb-4">
          {formatText('settings.language.default-language')}
        </h3>
        <div className="max-w-xs">
          <Select
            options={languageOptions}
            selected={settings.language}
            onChange={handleLanguageChange}
            placeholder={formatText('settings.language.select-language')}
          />
        </div>
      </div>

      {/* Date Format and Currency */}
      <div className="flex flex-wrap gap-12">
        {/* Date Format */}
        <div>
          <RadioButtonGroup
            name="dateFormat"
            label={formatText('settings.language.date-format')}
            labelClassName="text-base font-semibold"
            options={dateFormatOptions}
            value={settings.dateFormat}
            onChange={handleDateFormatChange}
            direction="row"
            gap="gap-6"
          />
        </div>
      </div>
    </div>
  );
};

export default Language;
